// La vie étudiante et l'alternance (prologue) : inscriptions et révisions, petits boulots,
// logement étudiant, et pour les alternants, le rythme école-entreprise avec M. Lemoine.
import type { GameState } from '../../engine/state';
import { DAYS_PER_YEAR } from '../../engine/time';
import type { EventDef, EventParams } from '../events';
import { HOUSING, MILIEUX } from '../paths';
import { between, simpleEvent, type Effects } from './kit';

const studies = (state: GameState) => state.path === 'studies';
const alternance = (state: GameState) => state.path === 'alternance';
/** Un logement à soi : colocation ou studio. */
const ownHome = (state: GameState) => state.housing !== 'parents';
/** Une bourse d'études a été accordée (voir l'événement bourse_etudes). */
const boursier = (state: GameState) => (state.eventCounts.bourse_etudes ?? 0) > 0;

/** Les jours qui restent avant la fin des études ou de l'alternance. */
function daysLeft(state: GameState): number {
  return state.firstJobTargetDay === null ? 0 : state.firstJobTargetDay - state.day;
}

/** Le loyer actuel, et celui d'une chambre du CROUS : un cinquième de moins, arrondi à 5 €. */
function crousRent(state: GameState): EventParams {
  const current = Math.round(state.expenses.logement?.amount ?? HOUSING.coloc.rent);
  return { current, rent: Math.max(150, Math.round((current * 0.8) / 5) * 5) };
}

/** Signer pour la chambre du CROUS : le nouveau dépôt part, et l'ancien reviendra… en retard. */
function signCrous(params: EventParams, wellbeing: number): Effects {
  return {
    cash: -params.rent,
    setExpense: { id: 'logement', amount: params.rent },
    wellbeing,
    energy: -4,
    followUp: { event: 'depot_garantie_retard', days: 60 },
  };
}

/** La bourse sur critères sociaux, par mois. */
function scholarship(state: GameState): number {
  return MILIEUX[state.milieu].scholarship;
}

/** Le dépôt de garantie de l'ancien logement : un mois de loyer. */
function oldDeposit(state: GameState): number {
  return state.housing === 'studio' ? HOUSING.studio.rent : HOUSING.coloc.rent;
}

export const EVENTS: EventDef[] = [
  // Les études : les coups durs.
  simpleEvent({
    id: 'cvec_rentree',
    kind: 'imprevu',
    stages: ['prologue'],
    bad: true,
    shock: 4,
    season: [8, 9],
    cooldownDays: 300,
    condition: studies,
    params: () => ({ cost: 105 }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      // Les boursiers sont exonérés, mais doivent quand même demander leur attestation.
      { id: 'exoneration', visible: boursier, effects: { energy: -1 } },
    ],
  }),
  simpleEvent({
    id: 'sac_vole_bibliotheque',
    kind: 'imprevu',
    stages: ['prologue'],
    bad: true,
    shock: 7,
    maxCount: 1,
    params: (state) => ({ cost: between(state, 90, 150), low: between(state, 30, 50) }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'compte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'minimum', effects: (_, p) => ({ cash: -p.low, energy: -3 }) },
    ],
  }),
  simpleEvent({
    id: 'cle_usb_illisible',
    kind: 'imprevu',
    stages: ['prologue'],
    bad: true,
    shock: 6,
    maxCount: 1,
    params: (state) => ({ cost: between(state, 60, 120) }),
    choices: [
      { id: 'recuperer', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'refaire', effects: { energy: -6, wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'stage_annule',
    kind: 'imprevu',
    stages: ['prologue'],
    bad: true,
    shock: 7,
    maxCount: 1,
    // De la deuxième à la quatrième année.
    condition: (state) => studies(state) && daysLeft(state) > DAYS_PER_YEAR && daysLeft(state) <= 4 * DAYS_PER_YEAR,
    params: (state) => ({ cost: between(state, 250, 350), low: between(state, 60, 100) }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'compte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'sous_louer', effects: (_, p) => ({ cash: -p.low, energy: -4 }) },
    ],
  }),
  simpleEvent({
    id: 'rattrapage_billet',
    kind: 'imprevu',
    stages: ['prologue'],
    bad: true,
    shock: 6,
    season: [6, 7],
    cooldownDays: 300,
    maxCount: 2,
    condition: (state) => studies(state) && ownHome(state),
    params: (state) => ({ cost: between(state, 70, 110), low: between(state, 30, 45) }),
    choices: [
      { id: 'billet', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'covoiturage', effects: (_, p) => ({ cash: -p.low, energy: -4 }) },
    ],
  }),

  // Les études : les coups de chance.
  simpleEvent({
    id: 'colis_des_parents',
    kind: 'chance',
    stages: ['prologue'],
    maxCount: 2,
    condition: ownHome,
    params: (state) => ({ gain: between(state, 30, 60) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'diner', effects: { wellbeing: 3 } },
    ],
  }),
  simpleEvent({
    id: 'experience_remuneree',
    kind: 'chance',
    stages: ['prologue'],
    condition: studies,
    params: (state) => ({ gain: between(state, 20, 40) }),
    choices: [
      { id: 'participer', effects: (_, p) => ({ cash: p.gain, energy: -1 }) },
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, energy: -1, wellbeing: 1 }) },
      { id: 'reviser', effects: { xp: { skill: 'organisation', amount: 5 } } },
    ],
  }),
  simpleEvent({
    id: 'prix_concours_fac',
    kind: 'chance',
    pleasureCategory: 'repas',
    stages: ['prologue'],
    maxCount: 1,
    condition: studies,
    params: (state) => ({ gain: between(state, 150, 250), fete: between(state, 40, 70) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 2 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain, wellbeing: 1 }) },
      { id: 'feter', effects: (_, p) => ({ cash: p.gain - p.fete, pleasure: 3 }) },
    ],
  }),
  simpleEvent({
    id: 'aide_ponctuelle_crous',
    kind: 'chance',
    stages: ['prologue'],
    maxCount: 1,
    condition: (state) => studies(state) && state.cash < 0,
    params: (state) => ({ gain: between(state, 150, 300) }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: p.gain, wellbeing: 2 }) },
      { id: 'budget', effects: (_, p) => ({ cash: p.gain, energy: -2, page: 'budget', knowledge: 1 }) },
    ],
  }),

  // Les études : les tentations.
  simpleEvent({
    id: 'revisions_terrasse',
    kind: 'tentation',
    pleasureCategory: 'repas',
    stages: ['prologue'],
    season: [5, 6],
    cooldownDays: 300,
    condition: studies,
    params: (state) => ({ cost: between(state, 15, 30), low: between(state, 5, 10) }),
    choices: [
      { id: 'terrasse', effects: (_, p) => ({ cash: -p.cost, pleasure: 3 }) },
      {
        id: 'une_heure',
        effects: (_, p) => ({ cash: -p.low, pleasure: 1, energy: -1, xp: { skill: 'organisation', amount: 10 } }),
      },
      { id: 'reviser', effects: { energy: -3, wellbeing: -1, xp: { skill: 'organisation', amount: 20 } } },
    ],
  }),
  simpleEvent({
    id: 'week_end_integration',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ['prologue'],
    season: [9, 10],
    maxCount: 1,
    // La première année seulement.
    condition: (state) => studies(state) && daysLeft(state) > 4 * DAYS_PER_YEAR,
    params: (state) => ({ cost: between(state, 120, 180) }),
    choices: [
      { id: 'partir', effects: (_, p) => ({ cash: -p.cost, pleasure: 4, energy: -4 }) },
      { id: 'benevole', effects: { energy: -5, wellbeing: 3, xp: { skill: 'organisation', amount: 15 } } },
      { id: 'rester', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'voyage_de_promo',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ['prologue'],
    maxCount: 1,
    // À partir de la deuxième année.
    condition: (state) => studies(state) && daysLeft(state) <= 4 * DAYS_PER_YEAR,
    params: (state) => ({ cost: between(state, 220, 300), low: between(state, 120, 160) }),
    choices: [
      { id: 'partir', effects: (_, p) => ({ cash: -p.cost, pleasure: 5, energy: -2 }) },
      { id: 'gateaux', effects: (_, p) => ({ cash: -p.low, pleasure: 5, energy: -5 }) },
      { id: 'rester', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'camion_burgers',
    kind: 'tentation',
    pleasureCategory: 'repas',
    stages: ['prologue'],
    maxCount: 1,
    condition: studies,
    params: (state) => ({ cost: between(state, 9, 12), monthly: between(state, 20, 35) }),
    choices: [
      { id: 'habitude', effects: (_, p) => ({ addExpense: { id: 'courses', amount: p.monthly }, pleasure: 3 }) },
      { id: 'une_fois', effects: (_, p) => ({ cash: -p.cost, pleasure: 1 }) },
      { id: 'gamelle', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'papeterie_campus',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue'],
    maxCount: 2,
    condition: studies,
    params: (state) => ({ cost: between(state, 25, 45), low: between(state, 5, 10) }),
    choices: [
      { id: 'tout', effects: (_, p) => ({ cash: -p.cost, pleasure: 2 }) },
      { id: 'bloc', effects: (_, p) => ({ cash: -p.low, pleasure: 1 }) },
      { id: 'finir' },
    ],
  }),

  // Les études : le quotidien et les petits boulots.
  simpleEvent({
    id: 'restaurant_universitaire',
    kind: 'quotidien',
    stages: ['prologue'],
    maxCount: 1,
    condition: studies,
    params: (state) => ({ saving: between(state, 20, 35) }),
    choices: [
      { id: 'ru', effects: (_, p) => ({ addExpense: { id: 'courses', amount: -p.saving }, wellbeing: -1 }) },
      { id: 'habitudes' },
    ],
  }),
  simpleEvent({
    id: 'cours_particuliers_maths',
    kind: 'quotidien',
    stages: ['prologue'],
    params: (state) => {
      const gain = between(state, 80, 140);
      return { gain, half: Math.round(gain / 2) };
    },
    choices: [
      {
        id: 'donner',
        effects: (_, p) => ({ cash: p.gain, energy: -3, xp: { skill: 'communication', amount: 15 } }),
      },
      {
        id: 'partager',
        effects: (_, p) => ({ cash: p.half, energy: -1, xp: { skill: 'communication', amount: 8 } }),
      },
      { id: 'decliner' },
    ],
  }),
  simpleEvent({
    id: 'livraison_velo_pluie',
    kind: 'quotidien',
    stages: ['prologue'],
    condition: (state) => state.hasBike,
    params: (state) => ({
      gain: between(state, 90, 150),
      repair: between(state, 25, 40),
      low: between(state, 30, 50),
    }),
    choices: [
      // Les plaquettes de frein usées sous la pluie se paient sur la recette.
      { id: 'pluie', effects: (_, p) => ({ cash: p.gain - p.repair, energy: -5 }) },
      { id: 'dimanche', effects: (_, p) => ({ cash: p.low, energy: -2 }) },
      { id: 'sec' },
    ],
  }),
  simpleEvent({
    id: 'baby_sitting_samedi',
    kind: 'quotidien',
    stages: ['prologue'],
    params: (state) => ({ gain: between(state, 40, 60), cost: between(state, 15, 25) }),
    choices: [
      { id: 'garder', effects: (_, p) => ({ cash: p.gain, wellbeing: -1 }) },
      { id: 'fete', effects: (_, p) => ({ cash: -p.cost, wellbeing: 3, energy: -2 }) },
      { id: 'les_deux', effects: (_, p) => ({ cash: p.gain - p.cost, wellbeing: 1, energy: -4 }) },
    ],
  }),
  simpleEvent({
    id: 'partiels_ou_reviser',
    kind: 'quotidien',
    stages: ['prologue'],
    season: [12, 1],
    cooldownDays: 300,
    condition: studies,
    params: (state) => ({ cost: between(state, 12, 25) }),
    choices: [
      { id: 'bibliotheque', effects: { energy: -3, xp: { skill: 'organisation', amount: 15 } } },
      { id: 'cafe', effects: (_, p) => ({ cash: -p.cost, wellbeing: 2, xp: { skill: 'organisation', amount: 15 } }) },
      { id: 'groupe', effects: { energy: -2, wellbeing: 1, xp: { skill: 'communication', amount: 15 } } },
    ],
  }),
  simpleEvent({
    id: 'colonie_vacances',
    kind: 'quotidien',
    stages: ['prologue'],
    condition: studies,
    params: (state) => ({ gain: between(state, 220, 320) }),
    choices: [
      {
        id: 'partir',
        effects: (_, p) => ({ cash: p.gain, energy: -5, xp: { skill: 'communication', amount: 10 } }),
      },
      { id: 'rester' },
    ],
  }),
  simpleEvent({
    id: 'aide_logement_etudiant',
    kind: 'quotidien',
    stages: ['prologue'],
    maxCount: 1,
    condition: ownHome,
    params: (state) => ({ aide: between(state, 50, 90) }),
    choices: [
      {
        id: 'demander',
        effects: (_, p) => ({ addExpense: { id: 'logement', amount: -p.aide }, energy: -4, knowledge: 1 }),
      },
      { id: 'laisser' },
    ],
  }),
  simpleEvent({
    id: 'bde_tresorerie',
    kind: 'quotidien',
    stages: ['prologue'],
    maxCount: 1,
    condition: studies,
    params: (state) => ({ budget: 8000, cost: between(state, 10, 20) }),
    choices: [
      { id: 'tresorerie', effects: { energy: -3, xp: { skill: 'organisation', amount: 20 }, page: 'budget' } },
      { id: 'adherer', effects: (_, p) => ({ cash: -p.cost, wellbeing: 2 }) },
      { id: 'passer' },
    ],
  }),
  simpleEvent({
    id: 'troupe_theatre',
    kind: 'quotidien',
    pleasureCategory: 'loisirs',
    stages: ['prologue'],
    maxCount: 1,
    condition: studies,
    params: (state) => ({ cost: between(state, 20, 40), ticket: between(state, 5, 8) }),
    choices: [
      {
        id: 'rejoindre',
        effects: (_, p) => ({
          cash: -p.cost,
          wellbeing: 2,
          energy: -2,
          xp: { skill: 'communication', amount: 20 },
        }),
      },
      { id: 'spectacle', effects: (_, p) => ({ cash: -p.ticket, pleasure: 1 }) },
      { id: 'passer' },
    ],
  }),
  simpleEvent({
    id: 'logiciel_memoire',
    kind: 'quotidien',
    stages: ['prologue'],
    weight: 2,
    maxCount: 1,
    // Les deux dernières années, celles du mémoire.
    condition: (state) => studies(state) && daysLeft(state) <= 2 * DAYS_PER_YEAR,
    params: (state) => ({ cost: between(state, 60, 120) }),
    choices: [
      { id: 'acheter', effects: (_, p) => ({ cash: -p.cost, xp: { skill: 'technique', amount: 10 } }) },
      // La licence étudiante est gratuite, mais elle met trois semaines à arriver.
      { id: 'fac', effects: { energy: -1, wellbeing: -2, xp: { skill: 'technique', amount: 10 } } },
      { id: 'libre', effects: { energy: -4, xp: { skill: 'technique', amount: 20 } } },
    ],
  }),

  // Les études : les grandes étapes.
  simpleEvent({
    id: 'stage_obligatoire',
    kind: 'etape',
    stages: ['prologue'],
    weight: 2,
    maxCount: 1,
    // La troisième ou la quatrième année.
    condition: (state) => studies(state) && daysLeft(state) > DAYS_PER_YEAR && daysLeft(state) <= 3 * DAYS_PER_YEAR,
    params: (state) => ({ cost: between(state, 300, 450), gain: between(state, 400, 600) }),
    page: 'capital_humain',
    choices: [
      {
        id: 'paris',
        effects: (_, p) => ({ cash: -p.cost, wellbeing: 2, xp: { skill: 'technique', amount: 30 } }),
      },
      { id: 'local', effects: (_, p) => ({ cash: p.gain, xp: { skill: 'technique', amount: 15 } }) },
    ],
  }),
  simpleEvent({
    id: 'semestre_etranger',
    kind: 'etape',
    stages: ['prologue'],
    weight: 2,
    maxCount: 1,
    // La deuxième ou la troisième année.
    condition: (state) =>
      studies(state) && daysLeft(state) > 2 * DAYS_PER_YEAR && daysLeft(state) <= 4 * DAYS_PER_YEAR,
    params: (state) => ({ cost: between(state, 250, 400) }),
    choices: [
      {
        id: 'partir',
        effects: (_, p) => ({ cash: -p.cost, wellbeing: 5, xp: { skill: 'communication', amount: 25 } }),
      },
      {
        id: 'livret',
        effects: (_, p) => ({ livret: -p.cost, wellbeing: 5, xp: { skill: 'communication', amount: 25 } }),
      },
      { id: 'rester', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'chambre_crous',
    kind: 'etape',
    stages: ['prologue'],
    maxCount: 1,
    condition: (state) => studies(state) && ownHome(state) && (state.expenses.logement?.amount ?? 0) >= 400,
    params: crousRent,
    choices: [
      { id: 'signer', effects: (_, p) => signCrous(p, -2) },
      { id: 'visiter', effects: { energy: -1, followUp: { event: 'chambre_crous_visite', days: 3 } } },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    // Trois jours après la proposition, pour qui a voulu visiter d'abord.
    id: 'chambre_crous_visite',
    kind: 'etape',
    stages: ['prologue'],
    manual: true,
    params: crousRent,
    choices: [
      { id: 'signer', effects: (_, p) => signCrous(p, -1) },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    // Deux mois après l'entrée au CROUS : l'ancien propriétaire tarde à rendre le dépôt.
    id: 'depot_garantie_retard',
    kind: 'quotidien',
    stages: ['prologue'],
    manual: true,
    params: (state) => {
      const deposit = oldDeposit(state);
      // La loi majore le dépôt de 10 % du loyer par mois de retard commencé.
      return { deposit, penalty: Math.round(deposit * 0.1) };
    },
    choices: [
      {
        id: 'mise_en_demeure',
        effects: (_, p) => ({ cash: p.deposit + p.penalty, energy: -3, knowledge: 1 }),
      },
      { id: 'relancer', effects: (_, p) => ({ cash: p.deposit, energy: -1 }) },
    ],
  }),
  simpleEvent({
    id: 'remise_des_diplomes',
    kind: 'etape',
    stages: ['prologue'],
    weight: 5,
    maxCount: 1,
    // Les derniers mois, en études comme en alternance.
    condition: (state) => daysLeft(state) <= 240,
    params: (state) => ({ cost: between(state, 30, 50), shift: between(state, 50, 80) }),
    choices: [
      { id: 'ceremonie', effects: (_, p) => ({ cash: -p.cost, wellbeing: 6 }) },
      { id: 'simple', effects: { wellbeing: 3 } },
      { id: 'travail', effects: (_, p) => ({ cash: p.shift, wellbeing: -2 }) },
    ],
  }),

  // L'alternance : les coups durs.
  simpleEvent({
    id: 'semaine_ecole_loin',
    kind: 'imprevu',
    stages: ['prologue'],
    bad: true,
    shock: 5,
    maxCount: 1,
    condition: alternance,
    params: (state) => ({ cost: between(state, 120, 180), low: between(state, 50, 80) }),
    choices: [
      {
        id: 'hotel',
        effects: (_, p) => ({ cash: -p.cost, followUp: { event: 'nuits_ecole_remboursees', days: 30 } }),
      },
      {
        id: 'livret',
        effects: (_, p) => ({ livret: -p.cost, followUp: { event: 'nuits_ecole_remboursees', days: 30 } }),
      },
      { id: 'train', effects: (_, p) => ({ cash: -p.low, energy: -5 }) },
    ],
  }),
  simpleEvent({
    // Un mois après les nuits payées près de l'école.
    id: 'nuits_ecole_remboursees',
    kind: 'chance',
    stages: ['prologue'],
    manual: true,
    params: (state) => ({ gain: between(state, 40, 70) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
    ],
  }),
  simpleEvent({
    id: 'certification_anglais',
    kind: 'imprevu',
    stages: ['prologue'],
    bad: true,
    shock: 6,
    maxCount: 1,
    condition: alternance,
    params: (state) => ({ cost: between(state, 110, 150) }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'ecole', effects: { energy: -4, wellbeing: -1, xp: { skill: 'communication', amount: 15 } } },
    ],
  }),

  // L'alternance : les coups de chance.
  simpleEvent({
    id: 'aide_permis_apprentis',
    kind: 'chance',
    stages: ['prologue'],
    maxCount: 1,
    condition: (state) => alternance(state) && !state.upgrades.includes('permis'),
    params: () => ({ gain: 500 }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, energy: -1, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain, energy: -1 }) },
      { id: 'attendre' },
    ],
  }),
  simpleEvent({
    id: 'forfait_velo_entreprise',
    kind: 'chance',
    pleasureCategory: 'achats',
    stages: ['prologue'],
    maxCount: 1,
    condition: (state) => alternance(state) && state.hasBike,
    params: (state) => ({ gain: between(state, 100, 250), equip: between(state, 30, 50) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'equiper', effects: (_, p) => ({ cash: p.gain - p.equip, pleasure: 2 }) },
    ],
  }),

  // L'alternance : les tentations.
  simpleEvent({
    id: 'places_spectacle_lemoine',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ['prologue'],
    maxCount: 1,
    condition: alternance,
    params: (state) => {
      const cost = between(state, 25, 40);
      return { cost, double: cost * 2 };
    },
    choices: [
      { id: 'deux', effects: (_, p) => ({ cash: -p.double, pleasure: 4 }) },
      { id: 'une', effects: (_, p) => ({ cash: -p.cost, pleasure: 3 }) },
      { id: 'decliner' },
    ],
  }),
  simpleEvent({
    id: 'voyage_fin_examens',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ['prologue'],
    maxCount: 1,
    condition: alternance,
    params: (state) => ({ cost: between(state, 160, 220) }),
    choices: [
      { id: 'partir', effects: (_, p) => ({ cash: -p.cost, pleasure: 5, energy: -3 }) },
      { id: 'attendre', effects: { followUp: { event: 'voyage_derniere_place', days: 12 } } },
      { id: 'rester' },
    ],
  }),
  simpleEvent({
    // Douze jours plus tard, pour qui a attendu : la place revendue peut coûter plus ou moins cher.
    id: 'voyage_derniere_place',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ['prologue'],
    manual: true,
    params: (state) => ({ late: between(state, 120, 260) }),
    choices: [
      { id: 'racheter', effects: (_, p) => ({ cash: -p.late, pleasure: 5, energy: -3 }) },
      { id: 'laisser' },
    ],
  }),

  // L'alternance : le quotidien, à l'école et au bureau.
  simpleEvent({
    id: 'rythme_alternance',
    kind: 'quotidien',
    stages: ['prologue'],
    condition: alternance,
    params: (state) => ({ cost: between(state, 25, 45) }),
    choices: [
      { id: 'pause', effects: { restDays: 30, wellbeing: 2 } },
      { id: 'tenir', effects: { fatigueDays: 45, xp: { skill: 'organisation', amount: 15 } } },
      { id: 'parents', visible: ownHome, effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
    ],
  }),
  simpleEvent({
    id: 'bulletin_paie_apprenti',
    kind: 'quotidien',
    stages: ['prologue'],
    maxCount: 1,
    condition: alternance,
    params: (state) => ({ net: Math.round(state.studentIncome || 850) }),
    page: 'budget',
    choices: [
      { id: 'decortiquer', effects: { energy: -2, knowledge: 1, xp: { skill: 'organisation', amount: 10 } } },
      { id: 'net' },
      { id: 'suzanne', effects: { wellbeing: 2 } },
    ],
  }),
  simpleEvent({
    id: 'conges_payes_alternance',
    kind: 'quotidien',
    pleasureCategory: 'voyages',
    stages: ['prologue'],
    maxCount: 1,
    condition: alternance,
    params: (state) => ({ cost: between(state, 30, 50), trip: between(state, 120, 180) }),
    choices: [
      { id: 'gite', effects: (_, p) => ({ cash: -p.trip, pleasure: 4 }) },
      { id: 'parents', visible: ownHome, effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
      { id: 'maison', effects: { wellbeing: 2 } },
    ],
  }),
  simpleEvent({
    id: 'aide_loyer_alternants',
    kind: 'quotidien',
    stages: ['prologue'],
    maxCount: 1,
    condition: (state) => alternance(state) && ownHome(state),
    params: (state) => ({ aide: between(state, 30, 60) }),
    choices: [
      {
        id: 'demander',
        effects: (_, p) => ({ addExpense: { id: 'logement', amount: -p.aide }, energy: -3, knowledge: 1 }),
      },
      { id: 'laisser' },
    ],
  }),
  simpleEvent({
    id: 'examens_alternance',
    kind: 'quotidien',
    stages: ['prologue'],
    condition: alternance,
    choices: [
      // Le contrat d'apprentissage donne cinq jours de congé pour préparer les examens.
      { id: 'conge', effects: { wellbeing: -1, xp: { skill: 'organisation', amount: 20 } } },
      { id: 'soirs', effects: { energy: -4, fatigueDays: 21, xp: { skill: 'organisation', amount: 15 } } },
      { id: 'impasse', effects: { wellbeing: 1, xp: { skill: 'organisation', amount: 5 } } },
    ],
  }),
  simpleEvent({
    id: 'tour_des_croissants',
    kind: 'travail',
    stages: ['prologue'],
    maxCount: 1,
    condition: alternance,
    params: (state) => ({ cost: between(state, 15, 25), low: between(state, 5, 9) }),
    choices: [
      { id: 'viennoiseries', effects: (_, p) => ({ cash: -p.cost, wellbeing: 2 }) },
      { id: 'gateau', effects: (_, p) => ({ cash: -p.low, energy: -2, wellbeing: 2 }) },
      { id: 'impasse', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'archives_sans_lemoine',
    kind: 'travail',
    stages: ['prologue'],
    maxCount: 1,
    condition: alternance,
    choices: [
      { id: 'proposer', effects: { energy: -2, xp: { skill: 'technique', amount: 15 } } },
      { id: 'tuteur', effects: { energy: -1, wellbeing: 1, xp: { skill: 'communication', amount: 10 } } },
      { id: 'attendre', effects: { wellbeing: -1, xp: { skill: 'organisation', amount: 5 } } },
    ],
  }),
  simpleEvent({
    id: 'pot_fin_alternance',
    kind: 'etape',
    stages: ['prologue'],
    weight: 5,
    maxCount: 1,
    // Les derniers mois du contrat d'apprentissage : c'est à l'apprenti d'offrir le pot.
    condition: (state) => alternance(state) && daysLeft(state) <= 150,
    params: (state) => ({ cost: between(state, 80, 140), low: between(state, 30, 50) }),
    choices: [
      { id: 'buffet', effects: (_, p) => ({ cash: -p.cost, wellbeing: 4 }) },
      { id: 'maison', effects: (_, p) => ({ cash: -p.low, energy: -3, wellbeing: 3 }) },
      { id: 'sans', effects: { wellbeing: -1 } },
    ],
  }),

  // Les études : la colocation, les démarches, et quelques façons de gagner (ou de perdre) de l'argent.
  simpleEvent({
    // La clause de solidarité du bail : tant que la chambre n'est pas reprise, la part du partant reste due.
    id: 'colocataire_depart',
    kind: 'imprevu',
    stages: ['prologue'],
    bad: true,
    shock: 6,
    maxCount: 1,
    condition: (state) => state.housing === 'coloc',
    params: (state) => {
      const share = Math.round(state.expenses.logement?.amount ?? HOUSING.coloc.rent);
      return { share, twoMonths: 2 * share };
    },
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.twoMonths }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.twoMonths }) },
      { id: 'visites', effects: (_, p) => ({ cash: -p.share, energy: -4, wellbeing: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'factures_coloc',
    kind: 'quotidien',
    stages: ['prologue'],
    maxCount: 1,
    condition: (state) => state.housing === 'coloc',
    params: (state) => ({ owed: between(state, 70, 110) }),
    choices: [
      { id: 'reclamer', effects: (_, p) => ({ cash: p.owed, wellbeing: -1 }) },
      { id: 'laisser' },
    ],
  }),
  simpleEvent({
    // L'abonnement de transport à moitié prix des étudiants et des apprentis : il faut le demander.
    // Le premier emploi remet le transport au plein tarif.
    id: 'tarif_etudiant_transports',
    kind: 'quotidien',
    stages: ['prologue'],
    maxCount: 1,
    condition: (state) => (studies(state) || alternance(state)) && (state.expenses.transport?.amount ?? 0) >= 20,
    params: (state) => {
      const current = Math.round(state.expenses.transport?.amount ?? 0);
      return { current, saving: Math.round(current / 2) };
    },
    choices: [
      { id: 'demander', effects: (_, p) => ({ addExpense: { id: 'transport', amount: -p.saving }, energy: -1 }) },
      { id: 'plus_tard' },
    ],
  }),
  simpleEvent({
    id: 'sport_fac',
    kind: 'quotidien',
    stages: ['prologue'],
    season: [9, 10],
    maxCount: 1,
    condition: studies,
    params: (state) => ({ fac: between(state, 25, 40), salle: between(state, 25, 35) }),
    choices: [
      { id: 'inscrire', effects: (_, p) => ({ cash: -p.fac, wellbeing: 3 }) },
      { id: 'pas_cette_annee' },
    ],
  }),
  simpleEvent({
    id: 'surveillance_examens',
    kind: 'travail',
    stages: ['prologue'],
    maxCount: 1,
    condition: studies,
    params: (state) => ({ gain: between(state, 130, 170) }),
    choices: [
      { id: 'accepter', effects: (_, p) => ({ cash: p.gain, energy: -4, wellbeing: -1 }) },
      { id: 'decliner' },
    ],
  }),
  simpleEvent({
    // Jusqu'à 25 ans, un étudiant peut rester rattaché au foyer fiscal de ses parents ; ses salaires de job
    // étudiant ne sont pas imposés dans la limite de trois SMIC mensuels par an.
    id: 'premiere_declaration',
    kind: 'quotidien',
    stages: ['prologue'],
    season: [4, 5],
    maxCount: 1,
    condition: studies,
    params: (state) => ({ merci: between(state, 60, 100) }),
    choices: [
      { id: 'rattache', visible: (state) => state.milieu !== 'modeste', effects: (_, p) => ({ cash: p.merci }) },
      { id: 'rattache_modeste', visible: (state) => state.milieu === 'modeste' },
      { id: 'propre', effects: { energy: -1 } },
    ],
  }),
  simpleEvent({
    // L'arnaque de la « mule » : prêter son compte pour faire transiter l'argent d'une escroquerie.
    id: 'compte_a_preter',
    kind: 'quotidien',
    stages: ['prologue'],
    maxCount: 1,
    params: () => ({ offer: 300, transfer: 2000, forward: 1700 }),
    page: 'arnaques',
    choices: [
      { id: 'signaler' },
      { id: 'accepter', effects: (_, p) => ({ cash: p.offer, followUp: { event: 'compte_bloque', days: 25 } }) },
    ],
  }),
  simpleEvent({
    id: 'compte_bloque',
    kind: 'imprevu',
    stages: ['prologue', 'stage1'],
    bad: true,
    shock: 8,
    manual: true,
    params: () => ({ clawback: 2000 }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: -p.clawback, energy: -3 }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.clawback, energy: -3 }) },
    ],
  }),
  simpleEvent({
    id: 'vendanges',
    kind: 'travail',
    stages: ['prologue'],
    season: [9],
    maxCount: 1,
    condition: studies,
    params: (state) => ({ gain: between(state, 700, 850) }),
    choices: [
      { id: 'vendanger', effects: (_, p) => ({ cash: p.gain, energy: -6, fatigueDays: 10 }) },
      { id: 'repos', effects: { wellbeing: 2 } },
    ],
  }),
  simpleEvent({
    // La bourse sur critères sociaux se redemande chaque année, avant la fin mai.
    id: 'dossier_social',
    kind: 'quotidien',
    stages: ['prologue'],
    season: [3, 4, 5],
    maxCount: 1,
    condition: (state) => studies(state) && boursier(state) && daysLeft(state) > 150,
    params: (state) => ({ lost: 2 * scholarship(state) }),
    choices: [
      { id: 'remplir', effects: { energy: -2 } },
      { id: 'plus_tard', effects: { followUp: { event: 'bourse_suspendue', days: 150 } } },
    ],
  }),
  simpleEvent({
    id: 'bourse_suspendue',
    kind: 'imprevu',
    stages: ['prologue'],
    bad: true,
    shock: 6,
    manual: true,
    // Les études finies entre-temps, la bourse n'a plus lieu d'être : la suite n'arrive pas.
    condition: (state) => studies(state) && boursier(state) && state.stage === 'prologue',
    params: (state) => ({ lost: 2 * scholarship(state) }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: -p.lost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.lost }) },
    ],
  }),
];
